import {getActivePersistenceUser,getPersistenceGeneration,persistenceWriteAllowed} from '../lib/persistenceIdentity';
import {manualFootageStorageKey,type ManualFootageOwner} from '../lib/manualFootageStorage';
import {fetchWithAuth} from '../lib/api';
import type {ContentCommandState} from './contentReviewService';

export type {ManualFootageOwner} from '../lib/manualFootageStorage';

export const MANUAL_FOOTAGE_MODE = 'alternate_matching_scene' as const;
export const DEFAULT_FOOTAGE_REASON = 'Replace the selected occurrence with another eligible clip matching this scene';
export const manualFootageEnabled = () => process.env.REACT_APP_CONTENT_REVIEW_MANUAL_FOOTAGE_ENABLED === '1';
export interface FootageOccurrence {scene_id:string;clip_id:string;clip_index:number;offset_sec:number;duration_sec:number;}
export interface ManualFootageSnapshot {
 package_id:string;fingerprint:string;editable:boolean;footage_targets:FootageOccurrence[];
 supported_selection_modes:Array<typeof MANUAL_FOOTAGE_MODE>;reason_usage:'audit_only';
}
export interface ManualFootageRequest {
 request_id:string;schema_version:1;fingerprint:string;scene_id:string;clip_id:string;clip_index:number;
 selection_mode:typeof MANUAL_FOOTAGE_MODE;reason:string;
}
export interface PendingFootageRequest {packageId:string;request:ManualFootageRequest;previousRequestId:string|null;confirmedState?:'queued'|'leased'|'retry'|'applied';}
export interface QueuedManualFootage extends ManualFootageRequest {state:'queued'|'leased'|'retry';reason_usage:'audit_only';}
const id = (v:unknown):v is string => typeof v === 'string' && /^[A-Za-z0-9_.:-]{1,160}$/.test(v);
const fingerprint = (v:unknown):v is string => typeof v === 'string' && /^[a-f0-9]{64}$/.test(v);
const index = (v:unknown):v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 10000;
export const validFootageReason = (v:unknown):v is string => typeof v === 'string' && !!v.trim()
 && Array.from(v).length <= 2000 && !v.includes('\0')
 && !Array.from(v).some(c => {const n=c.codePointAt(0)!;return n>=0xd800 && n<=0xdfff;});
const validConfirmedState = (v:unknown) => v===undefined || ['queued','leased','retry','applied'].includes(v as string);
const REQUEST_FIELDS = ['request_id','schema_version','fingerprint','scene_id','clip_id','clip_index','selection_mode','reason'] as const;
const validRequest = (r:ManualFootageRequest, queued=false) => r
 && Object.keys(r).every(key=>(REQUEST_FIELDS as readonly string[]).includes(key) || (queued && ['state','reason_usage'].includes(key)))
 && id(r.request_id) && r.request_id.length >= 8
 && r.schema_version === 1 && fingerprint(r.fingerprint) && id(r.scene_id) && id(r.clip_id)
 && index(r.clip_index) && r.selection_mode === MANUAL_FOOTAGE_MODE && validFootageReason(r.reason);
export function parseManualFootage(value:unknown, packageId:string):ManualFootageSnapshot {
 const r=value as ManualFootageSnapshot;
 if(!r || r.package_id!==packageId || !fingerprint(r.fingerprint) || typeof r.editable!=='boolean'
  || r.reason_usage!=='audit_only' || !Array.isArray(r.supported_selection_modes)
  || r.supported_selection_modes.length!==1 || r.supported_selection_modes[0]!==MANUAL_FOOTAGE_MODE
  || !Array.isArray(r.footage_targets) || r.footage_targets.length>200
  || new Set(r.footage_targets.map(t=>t?.clip_index)).size!==r.footage_targets.length
  || !r.footage_targets.every(t=>t && id(t.scene_id) && id(t.clip_id) && index(t.clip_index)
   && typeof t.offset_sec==='number' && Number.isFinite(t.offset_sec) && t.offset_sec>=0
   && typeof t.duration_sec==='number' && Number.isFinite(t.duration_sec) && t.duration_sec>0)) {
  throw new Error('Could not verify the footage occurrences.');
 }
 return r;
}
export function parseQueuedFootage(value:unknown, packageId:string):{package_id:string;queued:QueuedManualFootage|null} {
 const r=value as {package_id:string;queued:QueuedManualFootage|null};
 if(!r || r.package_id!==packageId || (r.queued!==null && (!validRequest(r.queued,true)
  || r.queued.reason_usage!=='audit_only' || !['queued','leased','retry'].includes(r.queued.state)))) {
  throw new Error('Could not verify the pending footage request.');
 }
 return r;
}
/** Explicit field comparison keeps queued metadata out of the immutable POST body. */
export const sameFootageRequest = (a:ManualFootageRequest,b:ManualFootageRequest) =>
 REQUEST_FIELDS.every(k=>a[k]===b[k]);
export function selectedFootage(snapshot:ManualFootageSnapshot, packageId:string, expectedFingerprint:string, selected:FootageOccurrence) {
 const target=snapshot.footage_targets.find(t=>t.clip_index===selected.clip_index);
 if(snapshot.package_id!==packageId || snapshot.fingerprint!==expectedFingerprint || !target
  || target.scene_id!==selected.scene_id || target.clip_id!==selected.clip_id
  || target.offset_sec!==selected.offset_sec || target.duration_sec!==selected.duration_sec) {
  throw new Error('This clip changed. Close and reload the reel before selecting it.');
 }
 return target;
}
export function getManualFootageOwner(userId?:string|null):ManualFootageOwner|null {
 if(!userId || getActivePersistenceUser()!==userId)return null;
 const generation=getPersistenceGeneration(userId);
 return Number.isSafeInteger(generation)&&generation>=0 ? {userId,generation} : null;
}
export const manualFootageOwnerCurrent=(owner:ManualFootageOwner|null):owner is ManualFootageOwner =>
 !!owner && persistenceWriteAllowed(owner.userId,owner.generation);
function requireOwner(owner:ManualFootageOwner|null):asserts owner is ManualFootageOwner {
 if(!manualFootageOwnerCurrent(owner))throw new Error('The signed-in account changed. Reload before checking this request.');
}
async function ownerFetch(id:string,suffix:string,owner:ManualFootageOwner,body?:ManualFootageRequest) {
 requireOwner(owner);
 const result=await fetchWithAuth<unknown>(`/admin/content/reviews/${encodeURIComponent(id)}/footage${suffix}`,{
  expectedUserId:owner.userId,requestStillCurrent:()=>manualFootageOwnerCurrent(owner),
  ...(body?{method:'POST',body:JSON.stringify(body)}:{})});
 requireOwner(owner);
 return result;
}
export const manualFootageService = {
 read:async(id:string,owner:ManualFootageOwner)=>parseManualFootage(await ownerFetch(id,'',owner),id),
 queued:async(id:string,owner:ManualFootageOwner)=>parseQueuedFootage(await ownerFetch(id,'/queued',owner),id),
 replace:async(id:string,input:ManualFootageRequest,owner:ManualFootageOwner)=>{
  if(!validRequest(input))throw new Error('Invalid footage request.');
  const r=await ownerFetch(id,'',owner,input) as {package_id:string;request_id:string;state:ContentCommandState;applied:boolean};
  if(!r || r.package_id!==id || r.request_id!==input.request_id
   || !['queued','leased','retry','applied','conflict','refused','exhausted'].includes(r.state)
   || r.applied!==(r.state==='applied'))throw new Error('Could not confirm the footage request.');
  return r;
 }
};

// Only unresolved request bodies survive refresh in this authenticated owner's
// current generation. This is an identity journal, never a background queue.
export function readPendingFootage(owner:ManualFootageOwner):PendingFootageRequest[] {
 requireOwner(owner);
 const raw=sessionStorage.getItem(manualFootageStorageKey(owner));
 if(!raw)return [];
 const rows=JSON.parse(raw) as PendingFootageRequest[];
 if(!Array.isArray(rows) || rows.length>200 || new Set(rows.map(r=>r?.packageId)).size!==rows.length
  || !rows.every(r=>r && id(r.packageId) && validRequest(r.request) && validConfirmedState(r.confirmedState)
   && (r.previousRequestId===null || id(r.previousRequestId))))throw new Error('Could not read saved request identities.');
 return rows;
}
export function savePendingFootage(row:PendingFootageRequest,owner:ManualFootageOwner) {
 requireOwner(owner);
 if(!id(row.packageId)||!validRequest(row.request)||!validConfirmedState(row.confirmedState)||(row.previousRequestId!==null&&!id(row.previousRequestId)))throw new Error('Invalid request identity.');
 const rows=readPendingFootage(owner);
 const existing=rows.find(r=>r.packageId===row.packageId);
 if(existing && (!sameFootageRequest(existing.request,row.request)||existing.previousRequestId!==row.previousRequestId))throw new Error('Another footage request is unresolved.');
 if(!existing && rows.length>=200)throw new Error('Saved request identity storage is full.');
 // An older queued read must never downgrade known applied status.
 const retained={...row,confirmedState:existing?.confirmedState==='applied'?'applied':row.confirmedState??existing?.confirmedState};
 sessionStorage.setItem(manualFootageStorageKey(owner),JSON.stringify([...rows.filter(r=>r.packageId!==row.packageId),retained]));
 if(!readPendingFootage(owner).some(r=>r.packageId===row.packageId && sameFootageRequest(r.request,row.request) && r.confirmedState===retained.confirmedState))throw new Error('Could not save request identity.');
}
export function clearPendingFootage(packageId:string,requestId:string,owner:ManualFootageOwner) {
 const rows=readPendingFootage(owner).filter(r=>r.packageId!==packageId || r.request.request_id!==requestId);
 const key=manualFootageStorageKey(owner);
 if(rows.length)sessionStorage.setItem(key,JSON.stringify(rows));else sessionStorage.removeItem(key);
}
